import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { UlmTextField } from "@components/ulm/ulm-text-field";
import { UlmButton } from "@components/ulm/ulm-button";
import { useCallback, useState } from "react";
import { useToast } from "@flat-ui";
import { EmailUtils } from "@stellar/utils";
import { RegisterableServer } from "@custom-types/server-types";
import { useGetRegisterableServer } from "@hooks/use-get-registerable-server";

interface Props {
  /** Callback to set the registerable server entity and validated email */
  setEmailAndServer: (email: string, server: RegisterableServer) => void;

  /** Optional initial email value */
  email?: string;

  /** Whether the input and button are disabled */
  isDisabled?: boolean;
}

/**
 * Component that renders a text field and button to validate the users email
 * address and check the status of the email in the backend.
 */
export function TrialEmail({
  setEmailAndServer,
  email,
  isDisabled = false,
}: Props): JSX.Element {
  const { closeToast } = useToast();
  const { getRegisterableServer, isFetchingServer } =
    useGetRegisterableServer();

  const [value, setValue] = useState<string>(email ?? "");
  const [isError, setIsError] = useState<boolean>(false);

  const isButtonDisabled =
    value.trim() === "" || isError || isFetchingServer || isDisabled;

  const isInputDisabled = isFetchingServer || isDisabled;

  /**
   * @returns Whether the input has a valid email format
   * @param input The email input value to validate
   */
  function isValidEmailFormat(input: string): boolean {
    return EmailUtils.isValidEmail(input.trim());
  }

  /**
   * Validates the email format and checks in the backend
   * oif the email is associated with a registerable server entity.
   */
  const validateAndCheckEmail = useCallback(
    async (value: string): Promise<void> => {
      closeToast();

      const email = value.trim();

      if (!isValidEmailFormat(email)) {
        return setIsError(true);
      }

      const server = await getRegisterableServer(email);

      if (server) {
        setEmailAndServer(email, server);
      }
    },
    [closeToast, getRegisterableServer, setEmailAndServer]
  );

  /**
   * Callback triggered on every input change event to update the value state.
   * and clear any previous error state.
   */
  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsError(false);
    setValue(event.target.value);
  }, []);

  /**
   * Callback triggered on every key up event in the input field. If the key is Enter,
   * it triggers the validateAndCheckEmail function if the "enter" key is pressed.
   */
  const onKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        void validateAndCheckEmail(value);
      }
    },
    [validateAndCheckEmail, value]
  );

  return (
    <>
      <UlmInputLabel title="Email" />
      <UlmTextField
        value={value}
        type="email"
        name="email"
        autoComplete="email"
        isValidValue={isValidEmailFormat(value)}
        isError={isError}
        errorText={"Enter a valid email format"}
        placeholder={"Enter your email"}
        onChange={onChange}
        onKeyUp={onKeyUp}
        isDisabled={isInputDisabled}
      />
      <UlmButton
        disabled={isButtonDisabled}
        isLoading={isFetchingServer}
        isLoadingText="Verifying"
        onClick={() => validateAndCheckEmail(value)}
        sx={{
          marginTop: "8px",
        }}
      >
        Continue
      </UlmButton>
    </>
  );
}
